import * as actions from './organizationActions';
import { LOGOUT } from '../../auth/actions';

const initialState = {
  organizations: [],
  selectedOrganization: null,
  members: [],
  invitations: [],
  availableRoles: [],
  loading: false,
  error: null,
  success: null,
  loaded: false,
};

export default function organizationReducer(state = initialState, action) {
  switch (action.type) {
    // Get My Organization
    case actions.GET_MY_ORGANIZATION.REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };
    case actions.GET_MY_ORGANIZATION.SUCCESS: {
      // Handle both single organization and array of organizations
      const { data } = action.payload;
      let orgsData = [];

      if (Array.isArray(data)) {
        orgsData = data;
      } else if (data) {
        orgsData = [data];
      }

      const previousSelection = state.selectedOrganization;
      let nextSelection = null;

      // Preserve "All Organizations" special selection
      if (previousSelection?.id === 'all') {
        nextSelection = previousSelection;
      } else if (previousSelection?.id) {
        // If there was a previously selected organization (possibly from persistence),
        // try to find it in the fresh data from the backend
        const stillExists = orgsData.find(
          (org) => String(org.id) === String(previousSelection.id)
        );

        if (stillExists) {
          // Organization still exists - use fresh data from backend
          nextSelection = stillExists;
        } else {
          // Previously selected organization no longer exists (deleted or access revoked)
          // Fallback to first available organization
          nextSelection = orgsData.length > 0 ? orgsData[0] : null;
        }
      } else {
        // No previous selection - use first available
        nextSelection = orgsData.length > 0 ? orgsData[0] : null;
      }

      return {
        ...state,
        loading: false,
        organizations: orgsData,
        selectedOrganization: nextSelection,
        loaded: true,
      };
    }
    case actions.GET_MY_ORGANIZATION.FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        loaded: true,
      };

    // Create Organization
    case actions.CREATE_ORGANIZATION.REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
        success: null,
      };
    case actions.CREATE_ORGANIZATION.SUCCESS:
      return {
        ...state,
        loading: false,
        organizations: [...state.organizations, action.payload.data],
        selectedOrganization: action.payload.data,
        success: 'Organization created successfully',
        loaded: true,
      };
    case actions.CREATE_ORGANIZATION.FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    // Update Organization
    case actions.UPDATE_ORGANIZATION.REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
        success: null,
      };
    case actions.UPDATE_ORGANIZATION.SUCCESS: {
      const updatedOrg = action.payload.data;
      return {
        ...state,
        loading: false,
        organizations: state.organizations.map((org) =>
          String(org.id) === String(updatedOrg.id) ? updatedOrg : org
        ),
        selectedOrganization: updatedOrg,
        success: 'Organization updated successfully',
        loaded: true,
      };
    }
    case actions.UPDATE_ORGANIZATION.FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    // Delete Organization
    case actions.DELETE_ORGANIZATION.REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
        success: null,
      };
    case actions.DELETE_ORGANIZATION.SUCCESS: {
      const remainingOrgs = state.organizations.filter((org) => org.id !== action.payload.id);
      const previousSelection = state.selectedOrganization;
      let nextSelection = null;

      if (previousSelection?.id === 'all') {
        nextSelection = previousSelection;
      } else if (previousSelection?.id) {
        nextSelection =
          remainingOrgs.find((org) => String(org.id) === String(previousSelection.id)) ||
          (remainingOrgs.length > 0 ? remainingOrgs[0] : null);
      } else {
        nextSelection = remainingOrgs.length > 0 ? remainingOrgs[0] : null;
      }

      return {
        ...state,
        loading: false,
        organizations: remainingOrgs,
        selectedOrganization: nextSelection,
        success: 'Organization deleted successfully',
        loaded: true,
      };
    }
    case actions.DELETE_ORGANIZATION.FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    // Invite Member
    case actions.INVITE_MEMBER.REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
        success: null,
      };
    case actions.INVITE_MEMBER.SUCCESS:
      return {
        ...state,
        loading: false,
        invitations: [...state.invitations, action.payload.data],
        success: 'Invitation sent successfully',
      };
    case actions.INVITE_MEMBER.FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    // Accept Invitation
    case actions.ACCEPT_INVITATION.REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
        success: null,
      };
    case actions.ACCEPT_INVITATION.SUCCESS:
      return {
        ...state,
        loading: false,
        success: 'Invitation accepted successfully',
      };
    case actions.ACCEPT_INVITATION.FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    // Reject Invitation
    case actions.REJECT_INVITATION.REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
        success: null,
      };
    case actions.REJECT_INVITATION.SUCCESS:
      return {
        ...state,
        loading: false,
        success: 'Invitation rejected',
      };
    case actions.REJECT_INVITATION.FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    // Get Members
    case actions.GET_MEMBERS.REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };
    case actions.GET_MEMBERS.SUCCESS:
      return {
        ...state,
        loading: false,
        members: action.payload.data,
      };
    case actions.GET_MEMBERS.FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    // Change Member Role
    case actions.CHANGE_MEMBER_ROLE.REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
        success: null,
      };
    case actions.CHANGE_MEMBER_ROLE.SUCCESS:
      return {
        ...state,
        loading: false,
        members: state.members.map((member) =>
          member.id === action.payload.data.id ? action.payload.data : member
        ),
        success: 'Member role updated successfully',
      };
    case actions.CHANGE_MEMBER_ROLE.FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    // Remove Member
    case actions.REMOVE_MEMBER.REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
        success: null,
      };
    case actions.REMOVE_MEMBER.SUCCESS:
      return {
        ...state,
        loading: false,
        members: state.members.filter((member) => member.id !== action.payload.memberId),
        success: 'Member removed successfully',
      };
    case actions.REMOVE_MEMBER.FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    // Get Available Roles
    case actions.GET_AVAILABLE_ROLES.REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };
    case actions.GET_AVAILABLE_ROLES.SUCCESS:
      return {
        ...state,
        loading: false,
        availableRoles: action.payload.data,
      };
    case actions.GET_AVAILABLE_ROLES.FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    // UI State
    case actions.SET_SELECTED_ORGANIZATION:
      return {
        ...state,
        selectedOrganization: action.payload,
      };

    case actions.RESET_ORGANIZATION_STATE:
      return initialState;

    // Clean up persisted organization state on logout
    case LOGOUT.SUCCESS:
      return initialState;

    default:
      return state;
  }
}
